/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { Tag as AntTag, TableProps } from 'antd';
import { Key, useMemo, useState } from 'react';

import { Node } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Alert } from '@/primitives/Alert';
import { Button } from '@/primitives/Button';
import { Card } from '@/primitives/Card';
import { Flex } from '@/primitives/Flex';
import { Space } from '@/primitives/Space';
import { Table } from '@/primitives/Table';
import { Text } from '@/primitives/Text';

import './ReviewSelectedNodesStep.css';

export type ReviewSelectedNodesStepProps = {
  selectedNodes: Node[];
  nodesInFleet: Set<string>;
  onRemoveNodes: (nodeIds: string[]) => void;
  isLoading: boolean;
};

export function ReviewSelectedNodesStep({
  selectedNodes,
  nodesInFleet,
  onRemoveNodes,
  isLoading,
}: ReviewSelectedNodesStepProps) {
  const { t } = useMimicTranslation('nodes');
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const nodesInFleetCount = useMemo(() => {
    return selectedNodes.filter((n) => nodesInFleet.has(n.id)).length;
  }, [selectedNodes, nodesInFleet]);

  const columns: TableProps<Node>['columns'] = [
    {
      title: t('createFleetWizard.reviewNodes.columns.hostname'),
      dataIndex: 'hostname',
      key: 'hostname',
      render: (hostname: string, node: Node) => (
        <Flex align="center" gap="xs">
          <Text mono>{hostname || node.name}</Text>
          {nodesInFleet.has(node.id) && (
            <AntTag color="error" icon={<WarningOutlined />}>
              {t('createFleetWizard.reviewNodes.inFleet')}
            </AntTag>
          )}
        </Flex>
      ),
    },
    {
      title: t('createFleetWizard.reviewNodes.columns.operatingSystem'),
      dataIndex: 'operatingSystem',
      key: 'operatingSystem',
      render: (os: string) => <Text>{os}</Text>,
    },
    {
      title: t('createFleetWizard.reviewNodes.columns.connectivity'),
      dataIndex: 'connectivityState',
      key: 'connectivityState',
      render: (state: string) => <Text>{state}</Text>,
    },
    {
      title: t('createFleetWizard.reviewNodes.columns.lastSeen'),
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      render: (lastSeen: string) => <Text>{lastSeen ? new Date(lastSeen).toLocaleString() : '-'}</Text>,
    },
  ];

  const rowSelection: TableProps<Node>['rowSelection'] = {
    type: 'checkbox',
    selectedRowKeys,
    onChange: (keys: Key[]) => {
      setSelectedRowKeys(keys);
    },
    getCheckboxProps: (node: Node) => ({
      name: node.name,
    }),
  };

  const handleRemoveSelected = () => {
    onRemoveNodes(selectedRowKeys as string[]);
    setSelectedRowKeys([]);
  };

  const getRowClassName = (node: Node) => {
    if (nodesInFleet.has(node.id)) {
      return 'row-in-fleet';
    }
    return '';
  };

  return (
    <Card dataTestId="create-fleet-review-nodes" padding="sm" bordered style={{ width: '100%' }}>
      <Space dir="vertical" size="md" width="100%">
        <Flex justify="space-between" align="center" full>
          <Text type="subtle">{t('createFleetWizard.reviewNodes.description', { count: selectedNodes.length })}</Text>
          {selectedRowKeys.length > 0 && (
            <Button
              type="default"
              danger
              onClick={handleRemoveSelected}
              icon={<DeleteOutlined />}
              dataTestId="remove-selected-nodes"
            >
              {t('createFleetWizard.reviewNodes.remove')} ({selectedRowKeys.length})
            </Button>
          )}
        </Flex>

        {nodesInFleetCount > 0 && (
          <Alert
            type="error"
            message={t('createFleetWizard.reviewNodes.inFleetError', { count: nodesInFleetCount })}
            showIcon
          />
        )}

        <Table
          dataSource={selectedNodes}
          columns={columns}
          rowKey="id"
          rowSelection={rowSelection}
          loading={isLoading}
          pagination={false}
          rowClassName={getRowClassName}
        />
      </Space>
    </Card>
  );
}
